import { ReactNode } from 'react'
import { ProgressStatus, TProgressStatus } from '../ProgressStatus'
import { ColumnDisplay, ColumnEditing, ColumnMetadataConfig } from '../types-v4'
import { isProgressStatusArray, isTitleValueObject } from '../utils'
import { RenderNumberArray, RenderStringArray } from './arrayRenderers'
import { RenderNumberArrayBadges, RenderSingleBadge, RenderStringArrayBadges } from './badgeRenderers'
import { type CascadingValue, RenderCascadingField } from './RenderCascadingField'
import { RenderIcon } from './RenderIcon'
import { RenderStringWithIcon, RenderDate, RenderNumber, RenderBoolean, RenderString } from './primitiveRenderers'

/**
 * Main cell renderer that delegates to specific renderers based on column configuration
 */
export const RenderCell = ({
  display,
  editing,
  metadata,
  value,
}: {
  display?: ColumnDisplay
  editing?: ColumnEditing
  metadata?: ColumnMetadataConfig
  value: unknown
}): ReactNode => {
  if (value === null || value === undefined) {
    return null
  }
  // ProgressStatus component
  if (display?.component === 'ProgressStatus' && isProgressStatusArray(value)) {
    return <ProgressStatus value={value as TProgressStatus[]} />
  }

  // Badge component
  if (display?.component === 'Badge') {
    const { type, items } = editing ?? {}
    // Handle array badges
    if (type === 'array') {
      if (items?.type === 'string') {
        return <RenderStringArrayBadges values={value as string[]} />
      }
      if (items?.type === 'number') {
        return <RenderNumberArrayBadges values={value as number[]} formatter={display?.formatter} />
      }
    }
    // Handle single badge
    return <RenderSingleBadge value={value as string} metadata={metadata} />
  }

  // Icon component
  if (display?.component === 'Icon') {
    return <RenderIcon value={value} metadata={metadata} />
  }

  // Array types (non-badge)
  if (editing?.type === 'array' && display?.component !== 'Badge') {
    if (editing.items?.type === 'string') {
      return <RenderStringArray values={value as string[]} />
    }
    if (editing.items?.type === 'number') {
      return <RenderNumberArray values={value as number[]} formatter={display?.formatter} />
    }
    return JSON.stringify(value)
  }

  // Title-value objects
  if (isTitleValueObject(value)) {
    return (value as { title: string }).title
  }

  // Cascading fields
  if (
    editing?.type === 'cascading' &&
    editing?.fieldLabels &&
    editing?.options &&
    typeof value === 'object' &&
    value !== null
  ) {
    return (
      <RenderCascadingField
        value={value as CascadingValue}
        fieldLabels={editing.fieldLabels}
        options={editing.options}
        shouldTruncate={display?.truncate}
      />
    )
  }

  // Generic objects
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  // String with icon
  if (display?.icon) {
    return <RenderStringWithIcon value={value as string} iconName={display.icon} />
  }

  // Date/DateTime
  if (editing?.type === 'string' && ['date', 'datetime', 'date-time'].includes(editing?.format ?? '')) {
    return <RenderDate value={value as string} formatter={display?.formatter} />
  }

  // Number/Integer
  if (['number', 'integer'].includes(editing?.type ?? '')) {
    return <RenderNumber value={value as number} formatter={display?.formatter} />
  }

  // Boolean
  if (editing?.type === 'boolean' && typeof value === 'boolean') {
    return <RenderBoolean value={value as boolean} />
  }

  // String (default)
  return (
    <RenderString value={value ? (value as string) : ''} shouldTruncate={display?.truncate} maxWidth={display?.width} />
  )
}
